// The client half of the real-time STT wire contract (LAN-584).
//
// One WebSocket to the STT worker per utterance. The worker holds the Soniox
// key and the upstream socket; this side only speaks our own protocol:
//
//   → {"type":"start", ...}          first frame, always
//   → binary                         little-endian 16-bit mono PCM at 16 kHz
//   → {"type":"stop"}                the learner finished
//   → {"type":"cancel"}              throw the utterance away
//   ← {"type":"ready", ...}          upstream is open (audio before this is
//                                    buffered by the worker, so we may send
//                                    from the moment the socket opens)
//   ← {"type":"partial","text":...,"final_text":...}  display only
//   ← {"type":"result", ...}         the transcript, then close 1000
//   ← {"type":"error", ...}          retryable:true means "POST it instead"
//
// Every failure mode ends the same way for the caller: the promise stop()
// returns rejects, and the error carries `retryable`. The recording service
// then uploads the audio it buffered, so a broken socket costs latency, never
// an utterance.

const DEFAULT_CONNECT_TIMEOUT_MS = 4000;
const DEFAULT_TRANSCRIPTION_URL = 'https://stt.languatalk.com';
const SOCKET_OPEN = 1;

export function realtimeUrlFor(transcriptionUrl) {
  const base = String(transcriptionUrl || DEFAULT_TRANSCRIPTION_URL).replace(/\/+$/, '');
  return `${base.replace(/^http/, 'ws')}/realtime`;
}

export function buildRealtimeError(code, message, retryable = true) {
  const error = new Error(message || code);
  error.code = code;
  error.retryable = retryable !== false;
  return error;
}

class RealtimeSttConnection {
  // token: the STT token (LAN-1309), or a function resolving to one (or null).
  // It rides in the start frame, never in a header: Cloudflare logs request
  // headers. A string opens the socket at once; a function is awaited inside
  // the connect timeout, so a slow token fetch still ends in the fallback.
  constructor({ url, start = {}, onPartial = null, onSettled = null, connectTimeoutMs = DEFAULT_CONNECT_TIMEOUT_MS, socketFactory = null, token = null } = {}) {
    this.url = url;
    this.startMessage = start;
    this.onPartial = onPartial;
    // Fires once, whichever way the session ends. The caller uses it to drop a
    // session that finished on its own, so its refs cannot block the next one.
    this.onSettled = onSettled;
    this.connectTimeoutMs = connectTimeoutMs;
    this.socketFactory = socketFactory || ((target) => new WebSocket(target));
    this.token = token;
    this.sessionToken = null;

    this.socket = null;
    this.ready = false;
    this.settled = false;
    this.requestId = null;
    // Audio captured before the socket opened. The worker buffers what arrives
    // before its upstream is ready, but it cannot receive anything before the
    // socket itself is open.
    this.pendingAudio = [];
    this.connectPromise = null;
    this.resolveConnect = null;
    this.rejectConnect = null;
    this.connectTimer = null;

    this.resultPromise = new Promise((resolve, reject) => {
      this.resolveResult = resolve;
      this.rejectResult = reject;
    });
    // The caller only awaits this after stop(). A failure that arrives earlier
    // must not surface as an unhandled rejection.
    this.resultPromise.catch(() => {});
  }

  // Resolves when the worker says `ready`. Rejects on a bad start, a socket
  // error, or after connectTimeoutMs. A rejection is not the end of the
  // utterance: the caller keeps recording and falls back on stop().
  connect() {
    if (this.connectPromise) return this.connectPromise;

    this.connectPromise = new Promise((resolve, reject) => {
      this.resolveConnect = resolve;
      this.rejectConnect = reject;
    });
    // A caller that only cares about the result may ignore this promise: a
    // failed handshake is a fallback, not a crash.
    this.connectPromise.catch(() => {});

    this.connectTimer = setTimeout(() => {
      this.failConnect(buildRealtimeError('connect_timeout', 'Realtime transcription did not connect in time', true));
    }, this.connectTimeoutMs);

    if (typeof this.token === 'function') {
      Promise.resolve()
        .then(() => this.token())
        .catch(() => null)
        // Only while the connect is still pending: a token that arrives after the
        // timeout must not open a socket the caller has already given up on.
        .then((token) => { if (!this.settled && this.rejectConnect) this.openSocket(token); });
    } else {
      this.openSocket(this.token);
    }
    return this.connectPromise;
  }

  openSocket(token) {
    this.sessionToken = token || null;
    try {
      this.socket = this.socketFactory(this.url);
    } catch (error) {
      this.failSession(buildRealtimeError('socket_open_failed', error && error.message, true));
      return;
    }

    this.socket.binaryType = 'arraybuffer';
    this.socket.onopen = () => { this.handleOpen(); };
    this.socket.onmessage = (event) => { this.handleMessage(event); };
    this.socket.onerror = () => {
      this.failSession(buildRealtimeError('socket_error', 'Realtime transcription socket failed', true));
    };
    this.socket.onclose = () => { this.handleClose(); };
  }

  handleOpen() {
    this.sendJson(Object.assign({ type: 'start' }, this.startMessage, this.sessionToken ? { token: this.sessionToken } : {}));
    const queued = this.pendingAudio;
    this.pendingAudio = [];
    queued.forEach((chunk) => this.sendAudio(chunk));
  }

  // Accepts an Int16Array (or any typed array of PCM). Queued while the socket
  // is still opening so no speech is lost to the handshake.
  send(chunk) {
    if (this.settled || !chunk || chunk.length === 0) return;
    if (this.socket && this.socket.readyState === SOCKET_OPEN) {
      this.sendAudio(chunk);
    } else {
      this.pendingAudio.push(chunk);
    }
  }

  sendAudio(chunk) {
    const payload = chunk.buffer && chunk.byteLength !== undefined
      ? chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength)
      : chunk;
    try {
      this.socket.send(payload);
    } catch (error) {
      this.failSession(buildRealtimeError('send_failed', error && error.message, true));
    }
  }

  sendJson(message) {
    if (!this.socket || this.socket.readyState !== SOCKET_OPEN) return false;
    try {
      this.socket.send(JSON.stringify(message));
      return true;
    } catch (error) {
      this.failSession(buildRealtimeError('send_failed', error && error.message, true));
      return false;
    }
  }

  // Tells the worker the learner has finished and returns the transcript.
  stop() {
    if (!this.settled && !this.sendJson({ type: 'stop' })) {
      this.failSession(buildRealtimeError('not_connected', 'Realtime transcription socket was never open', true));
    }
    return this.resultPromise;
  }

  // Drops the utterance: the worker closes without a result and logs nothing
  // as a success.
  cancel() {
    this.sendJson({ type: 'cancel' });
    this.closeSocket();
    this.settle(() => this.rejectResult(buildRealtimeError('cancelled', 'Recording cancelled', false)));
  }

  handleMessage(event) {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch (error) {
      return; // Every worker → client frame is text JSON; anything else is noise.
    }
    if (!message || !message.type) return;

    switch (message.type) {
      case 'ready':
        this.ready = true;
        this.requestId = message.request_id || null;
        this.settleConnect();
        break;
      case 'partial':
        // A partial that arrives after the result, or after an error, is
        // already stale: the caller has moved on to the finished transcript.
        if (this.settled) break;
        // final_text is the part of text the worker will not revise.
        if (this.onPartial) this.onPartial(message.text || '', message.final_text || '');
        break;
      case 'result':
        // A result that arrives before `ready` still counts as a connection.
        this.settleConnect();
        this.settle(() => this.resolveResult(message));
        this.closeSocket();
        break;
      case 'error':
        this.failSession(buildRealtimeError(message.code || 'upstream_error', message.message, message.retryable));
        break;
      default:
        break;
    }
  }

  handleClose() {
    if (this.settled) return;
    this.failSession(buildRealtimeError('upstream_closed', 'Realtime transcription socket closed before a result', true));
  }

  settleConnect() {
    this.clearConnectTimer();
    const resolve = this.resolveConnect;
    this.resolveConnect = null;
    this.rejectConnect = null;
    if (resolve) resolve(this.requestId);
  }

  failConnect(error) {
    this.clearConnectTimer();
    if (this.rejectConnect) {
      const reject = this.rejectConnect;
      this.rejectConnect = null;
      this.resolveConnect = null;
      reject(error);
    }
  }

  failSession(error) {
    this.failConnect(error);
    this.settle(() => this.rejectResult(error));
    this.closeSocket();
  }

  settle(action) {
    if (this.settled) return;
    this.settled = true;
    action();
    if (this.onSettled) {
      const notify = this.onSettled;
      this.onSettled = null;
      try {
        notify();
      } catch (error) {
        console.error('Error notifying realtime session settle:', error);
      }
    }
  }

  clearConnectTimer() {
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
  }

  closeSocket() {
    // Nothing is still waiting on the handshake once the socket is gone, and a
    // live timer would hold the page (and the test runner) open.
    this.clearConnectTimer();
    const socket = this.socket;
    this.socket = null;
    this.pendingAudio = [];
    if (!socket) return;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    try {
      socket.close();
    } catch (error) {
      /* already closing */
    }
  }
}

export default RealtimeSttConnection;
